import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

const DEFAULT_SCRIPT_ID = 'sf-embedded-messaging-bootstrap'

const EmbeddedMessagingContext = createContext(null)

function ensureStyleTag(id) {
  let tag = document.getElementById(id)
  if (!tag) {
    tag = document.createElement('style')
    tag.id = id
    document.head.appendChild(tag)
  }
  return tag
}

/**
 * Fallback universal: oculta/enseña el launcher por CSS
 * (no depende de utilAPI.hideChatButton).
 */
function setLauncherVisibleByCss(visible) {
  const STYLE_ID = 'em-visibility-style'
  const style = ensureStyleTag(STYLE_ID)

  // [No verificado] Los selectores exactos pueden variar según el build de Salesforce.
  // Esto intenta cubrir los más habituales. Si no aplica, pega el HTML del launcher y lo ajusto.
  const css = visible
    ? ``
    : `
embedded-messaging,
embedded-messaging *[part="launcher"],
embedded-messaging *[part="button"],
.embeddedMessagingButton,
.embedded-messaging,
#embedded-messaging,
.esw-app,
.esw-floating-button {
  display: none !important
}
`
  style.textContent = css
}

/**
 * Carga "en segundo plano": cuando el navegador está libre.
 * Sin timeout (como pediste).
 * Fallback: setTimeout (por compatibilidad).
 */
function scheduleIdle(fn) {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => fn())
  } else {
    setTimeout(fn, 1500)
  }
}

export const EmbeddedMessagingProvider = ({
  children,
  config, // { orgId, deploymentName, baseUrl, scrt2Url, scriptUrl, language }
  enabled = true,
  defaultVisible = true,
}) => {
  const { orgId, deploymentName, baseUrl, scrt2Url, scriptUrl, language = 'es' } = config || {}

  const [ready, setReady] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [visible, setVisibleState] = useState(defaultVisible)

  const pendingFieldsRef = useRef([]) // cola de setHiddenPrechatFields
  const visibleRef = useRef(defaultVisible)
  const cleanupListenersRef = useRef(null)

  const applyVisibility = useCallback((nextVisible) => {
    visibleRef.current = nextVisible
    setVisibleState(nextVisible)

    const embedded = window.embeddedservice_bootstrap

    // Mostrar (si ya hay utilAPI)
    if (nextVisible && embedded?.utilAPI?.showChatButton) {
      embedded.utilAPI.showChatButton()
      setLauncherVisibleByCss(true)
      return
    }

    // [No verificado] hideChatButton puede no existir
    if (!nextVisible && embedded?.utilAPI?.hideChatButton) {
      embedded.utilAPI.hideChatButton()
      setLauncherVisibleByCss(false)
      return
    }

    // Fallback universal
    setLauncherVisibleByCss(nextVisible)
  }, [])

  const launchChat = useCallback(() => {
    const embedded = window.embeddedservice_bootstrap

    if (embedded?.utilAPI?.launchChat) {
      embedded.utilAPI.launchChat()
      return
    }
  }, [])

  const flushPendingFields = useCallback(() => {
    const embedded = window.embeddedservice_bootstrap
    if (!embedded?.prechatAPI?.setHiddenPrechatFields) return

    const queue = pendingFieldsRef.current
    if (!queue.length) return

    for (const fields of queue) {
      embedded.prechatAPI.setHiddenPrechatFields(fields)
    }
    pendingFieldsRef.current = []
  }, [])

  const setHiddenPrechatFields = useCallback((fields) => {
    const embedded = window.embeddedservice_bootstrap

    if (embedded?.prechatAPI?.setHiddenPrechatFields && window.__EMBEDDED_MESSAGING_READY__) {
      embedded.prechatAPI.setHiddenPrechatFields(fields)
      return
    }
    pendingFieldsRef.current.push(fields)
  }, [])

  const initEmbeddedMessaging = useCallback(() => {
    if (!enabled) return null

    if (!orgId || !deploymentName || !baseUrl || !scrt2Url || !scriptUrl) {
      console.error('[EmbeddedMessaging] Falta configuración')
      return null
    }

    // Ya inicializado
    if (window.__EMBEDDED_MESSAGING_INITIALIZED__) {
      setInitialized(true)
      // Aplica intención actual (visible/oculto) aunque aún no esté ready
      applyVisibility(visibleRef.current)
      return null
    }

    if (!window.embeddedservice_bootstrap) {
      console.error('[EmbeddedMessaging] no bootstrap')
      return null
    }

    const embedded = window.embeddedservice_bootstrap
    embedded.settings = embedded.settings || {}
    embedded.settings.language = language

    const onReady = () => {
      window.__EMBEDDED_MESSAGING_READY__ = true
      setReady(true)

      // Aplica la intención de visibilidad al estar listo
      applyVisibility(visibleRef.current)

      // Aplica campos pendientes
      flushPendingFields()
    }

    const onInitError = (event) => {
      console.error('[EmbeddedMessaging] INIT ERROR', event?.detail)
    }

    window.addEventListener('onEmbeddedMessagingReady', onReady)
    window.addEventListener('onEmbeddedMessagingInitializationError', onInitError)

    embedded.init(orgId, deploymentName, baseUrl, { scrt2URL: scrt2Url })

    window.__EMBEDDED_MESSAGING_INITIALIZED__ = true
    setInitialized(true)

    const cleanup = () => {
      window.removeEventListener('onEmbeddedMessagingReady', onReady)
      window.removeEventListener('onEmbeddedMessagingInitializationError', onInitError)
    }

    cleanupListenersRef.current = cleanup
    return cleanup
  }, [enabled, orgId, deploymentName, baseUrl, scrt2Url, scriptUrl, language, applyVisibility, flushPendingFields])

  useEffect(() => {
    if (!enabled) {
      applyVisibility(false)
      return
    }

    // Aplica visibilidad "intentada" desde el minuto 0 (aunque no esté ready)
    applyVisibility(visibleRef.current)

    // Script ya existe -> init directo
    if (document.getElementById(DEFAULT_SCRIPT_ID)) {
      const cleanup = initEmbeddedMessaging()
      return () => {
        if (typeof cleanup === 'function') cleanup()
      }
    }

    // Carga diferida (idle)
    scheduleIdle(() => {
      // Puede haberse deshabilitado mientras tanto
      if (enabled === 'false') return

      // Si alguien ya lo inyectó en paralelo
      if (document.getElementById(DEFAULT_SCRIPT_ID)) {
        initEmbeddedMessaging()
        return
      }

      const script = document.createElement('script')
      script.id = DEFAULT_SCRIPT_ID
      script.type = 'text/javascript'
      script.src = scriptUrl
      script.defer = true
      script.async = true
      script.onload = () => initEmbeddedMessaging()
      script.onerror = () => console.error('[EmbeddedMessaging] Error cargando script')

      document.body.appendChild(script)
    })

    // Limpieza listeners (no quitamos script)
    return () => {
      const cleanup = cleanupListenersRef.current
      if (typeof cleanup === 'function') cleanup()
      cleanupListenersRef.current = null
    }
  }, [enabled, scriptUrl, initEmbeddedMessaging, applyVisibility])

  // Si cambias visible desde fuera, lo aplicamos inmediatamente (CSS fallback) y luego en READY también.
  useEffect(() => {
    applyVisibility(visible)
  }, [visible, applyVisibility])

  const api = useMemo(
    () => ({
      ready,
      initialized,
      visible,
      launchChat,
      show: () => applyVisibility(true),
      hide: () => applyVisibility(false),
      setHiddenPrechatFields,
    }),
    [ready, initialized, visible, applyVisibility, setHiddenPrechatFields, launchChat],
  )

  return <EmbeddedMessagingContext.Provider value={api}>{children}</EmbeddedMessagingContext.Provider>
}

export const useEmbeddedMessaging = () => {
  const ctx = useContext(EmbeddedMessagingContext)
  if (!ctx) throw new Error('useEmbeddedMessaging debe usarse dentro de EmbeddedMessagingProvider')
  return ctx
}
